import { forwardRef, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@apollo/client";
import { AlertCircle, ArrowRight, Mail, Phone, X } from "lucide-react";
import { Avatar, Badge } from "../../components/ui";
import { GET_CLIENT_DETAIL } from "../../apollo/clients";
import { ManualSessionModal } from "../sessions/ManualSessionModal";
import {
  deriveTimeline,
  deriveStatus,
  primaryContact,
  getDisplayName,
  getSecondaryName,
  getInitials,
  formatSessionLabel,
  formatCurrency,
  CLIENT_STATUS_LABEL,
  CLIENT_STATUS_COLOR,
} from "../../data/clientDerive";
import type { DetailClient } from "../../data/clientDerive";
import { timeAgo } from "../../utils/sessionTime";

interface ClientDetailPaneProps {
  clientId: string;
  onClose: () => void;
  /** Current list query string (filters/search/sort/page), forwarded to the
   *  profile via router state so its "Back to clients" can restore this view. */
  listSearch?: string;
}

function formatMemberSince(isoDate: string): string {
  const d = new Date(isoDate);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);
}

function formatSessionDate(iso: string | null): string | null {
  if (!iso) return null;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return null;
  return formatSessionLabel(dt);
}

/**
 * Quick-view pane for a selected Client. Fetches its own data via
 * GET_CLIENT_DETAIL (computed stats + contacts + Client-attributed bookings
 * for the timeline). Uses forwardRef so the parent can scroll it into view.
 */
export const ClientDetailPane = forwardRef<
  HTMLElement,
  ClientDetailPaneProps
>(function ClientDetailPane({ clientId, onClose, listSearch }, ref) {
  const { data, loading, error, refetch } = useQuery(GET_CLIENT_DETAIL, {
    variables: { id: clientId },
    fetchPolicy: "cache-and-network",
  });

  const client: DetailClient | null = data?.Client ?? null;
  const [createOpen, setCreateOpen] = useState(false);

  if (error && !client) {
    return (
      <aside
        ref={ref}
        className="lg:col-span-4 bg-paper-hi rounded-lg shadow-card overflow-hidden h-fit lg:sticky lg:top-20"
      >
        <div className="p-5 text-center text-ink-4 text-[13px]">
          <div className="flex items-center justify-center gap-1.5 mb-1">
            <AlertCircle size={14} strokeWidth={2} className="text-rust" />
            <span className="text-ink-2 font-medium">Failed to load client details</span>
          </div>
          <p className="text-ink-4 text-[12px] mb-2">Something went wrong. Please try again.</p>
          <button
            onClick={onClose}
            className="text-tide hover:underline"
          >
            Close
          </button>
        </div>
      </aside>
    );
  }

  if (loading && !client) {
    return (
      <aside
        ref={ref}
        className="lg:col-span-4 bg-paper-hi rounded-lg shadow-card overflow-hidden h-fit lg:sticky lg:top-20"
      >
        <div className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-md bg-canvas animate-pulse flex-shrink-0" />
            <div className="flex-1">
              <div className="h-5 w-32 rounded bg-canvas animate-pulse mb-2" />
              <div className="h-3 w-24 rounded bg-canvas animate-pulse" />
            </div>
          </div>
          <div className="space-y-3 mt-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-3 w-full rounded bg-canvas animate-pulse"
              />
            ))}
          </div>
        </div>
      </aside>
    );
  }

  if (!client) {
    return (
      <aside
        ref={ref}
        className="lg:col-span-4 bg-paper-hi rounded-lg shadow-card overflow-hidden h-fit lg:sticky lg:top-20"
      >
        <div className="p-5 text-center text-ink-4 text-[13px]">
          Client not found.
          <button
            onClick={onClose}
            className="ml-2 text-tide hover:underline"
          >
            Close
          </button>
        </div>
      </aside>
    );
  }

  const status = deriveStatus(client);
  const displayName = getDisplayName(client);
  const secondaryName = getSecondaryName(client);
  const initials = getInitials(client);
  const contact = primaryContact(client);
  const isNew =
    !client.archivedAt &&
    new Date(client.createdAt).getTime() >
      Date.now() - 30 * 24 * 60 * 60 * 1000;
  const timeline = deriveTimeline(client, client.bookings ?? []);
  const lastSession = formatSessionDate(client.lastSessionAt);
  const nextSession = formatSessionDate(client.nextSessionAt);

  const statusCfg = CLIENT_STATUS_COLOR[status];

  return (
    <aside
      ref={ref}
      className="lg:col-span-4 bg-paper-hi rounded-lg shadow-card overflow-hidden h-fit lg:sticky lg:top-20"
    >
      {/* Header */}
      <div className="p-5 hair-b relative">
        <div className="absolute top-4 right-4">
          <button
            onClick={onClose}
            className="w-7 h-7 rounded hover:bg-paper-2 flex items-center justify-center text-ink-4"
            aria-label="Close detail pane"
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>
        <div className="flex items-start gap-3 mb-4 pr-8">
          <Avatar
            initials={initials}
            size="lg"
            className={
              isNew
                ? "bg-tide text-paper-hi !w-14 !h-14 !text-[18px] !rounded-md"
                : "!w-14 !h-14 !text-[18px] !rounded-md"
            }
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-0.5">
              <h3 className="font-display text-[22px] font-medium leading-tight">
                {displayName}
              </h3>
              {isNew && <Badge variant="spark">New</Badge>}
            </div>
            {secondaryName && (
              <div className="text-[12.5px] text-ink-4 mb-0.5 truncate">
                {secondaryName}
              </div>
            )}
            <Link
              to={`/clients/${clientId}`}
              state={{ from: listSearch }}
              className="inline-flex items-center gap-0.5 text-[11.5px] text-ink-4 hover:text-tide mb-1.5"
            >
              View profile
              <ArrowRight size={11} strokeWidth={2} />
            </Link>
            <div className="flex flex-col items-start gap-0.5 text-[12.5px]">
              <div
                className={`inline-flex items-center gap-1.5 font-medium ${statusCfg.text}`}
              >
                <div className={`dot ${statusCfg.dot}`} />
                {CLIENT_STATUS_LABEL[status]}
              </div>
              <span className="text-ink-4">
                Client since {formatMemberSince(client.createdAt)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCreateOpen(true)}
            disabled={!contact}
            className="flex-1 px-3 py-2 rounded-md bg-tide text-paper-hi text-[12.5px] font-medium hover:bg-tide-hi disabled:opacity-50 disabled:cursor-not-allowed"
          >
            New session
          </button>
          {contact && (
            <a
              href={`mailto:${contact.email}`}
              className="px-3 py-2 rounded-md hair border text-[12.5px] text-ink-2 hover:bg-paper-2 flex items-center gap-1.5"
            >
              <Mail size={13} strokeWidth={2} />
              Email
            </a>
          )}
        </div>
      </div>

      {/* People */}
      <div className="px-5 py-4 hair-b">
        <div className="text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-2">
          {client.peopleCount === 1 ? "Person" : `People (${client.peopleCount})`}
        </div>
        <div className="space-y-2.5 text-[12.5px]">
          {(client.people ?? []).map((c) => (
            <div key={c.id} className="min-w-0">
              <div className="text-ink-2 font-medium truncate">
                {getDisplayName(c)}
              </div>
              <div className="flex items-center justify-between gap-3">
                <a
                  href={`mailto:${c.email}`}
                  className="text-ink-4 hover:text-tide truncate"
                >
                  {c.email}
                </a>
                {c.phoneNumber && (
                  <a
                    href={`tel:${c.phoneNumber}`}
                    className="text-ink-4 num hover:text-tide flex-shrink-0"
                  >
                    {c.phoneNumber}
                  </a>
                )}
              </div>
            </div>
          ))}
          {(client.people ?? []).length === 0 && (
            <div className="text-ink-5">No people yet.</div>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="px-5 py-4 hair-b grid grid-cols-2 gap-3 text-center">
        <div>
          <div className="font-display text-[22px] font-medium num">
            {client.sessionsCount}
          </div>
          <div className="text-[11px] text-ink-4 uppercase tracking-wider mt-0.5">
            {client.sessionsCount === 1 ? "Session" : "Sessions"}
          </div>
        </div>
        <div className="hair-l">
          <div className="font-display text-[22px] font-medium num">
            {formatCurrency(client.lifetimeSpend)}
          </div>
          <div className="text-[11px] text-ink-4 uppercase tracking-wider mt-0.5">
            Lifetime
          </div>
        </div>
      </div>

      {/* Last / Next session */}
      <div className="px-5 py-4 hair-b space-y-2.5 text-[12.5px]">
        <div className="flex items-center justify-between">
          <span className="text-ink-4">Last session</span>
          <span className="text-ink-2">{lastSession ?? "--"}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-ink-4">Next session</span>
          <span className="text-ink-2">{nextSession ?? "--"}</span>
        </div>
      </div>

      {/* Timeline */}
      <div className="px-5 py-4">
        <h4 className="text-[13px] font-semibold mb-3">Timeline</h4>
        {timeline.length > 0 ? (
          <div className="space-y-3 text-[12.5px]">
            {timeline.slice(0, 10).map((ev) => (
              <div key={ev.id} className="flex gap-3">
                <div
                  className={`w-2 h-2 rounded-full ${ev.dotColor} mt-1.5 flex-shrink-0`}
                />
                <div className="flex-1">
                  <div className="text-ink-2 font-medium">{ev.title}</div>
                  <div className="text-ink-4 text-[11.5px] mt-0.5">
                    {ev.detail}
                  </div>
                  <div className="text-ink-5 text-[11px] mt-0.5">
                    {timeAgo(ev.date)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-[12.5px] text-ink-4">No activity yet.</div>
        )}
        {contact?.phoneNumber && (
          <div className="mt-3 hair-t pt-3 flex items-center justify-end">
            <a
              href={`tel:${contact.phoneNumber}`}
              className="text-[11.5px] text-ink-4 hover:text-ink-2 flex items-center gap-1"
            >
              <Phone size={11} strokeWidth={2} />
              Call client
            </a>
          </div>
        )}
      </div>

      {contact && (
        <ManualSessionModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          presetCustomerId={contact.id}
          onCreated={() => refetch()}
        />
      )}
    </aside>
  );
});
